import _ from 'lodash';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { Accessors } from 'vue/types/options';
import { editName } from '@/main/store/listEditTypes';
import { PageModule, RootStore } from '@/main/store/@types/store.d';
import { DropdownList } from '@/main/store/@types/dropdown.d';
import { Table } from '@/main/utils/table';
import { EditStoreBase, EditStore, EditStoreProps } from '@/main/store/@types/edit.d';

import { getterAndSetterFromKeyDvm, getterFromKeyDvm, initStore, mapActionsDvm, mapMutationsDvm, MappedGetters, Page } from './page';
import { Edit } from './edit';


/**
 * Generates `computed` properties for Vue components so that they can be accessed
 * directly as `this.propName`, not just indirectly with `this.$store.[ ... ].propName`.
 *
 * @param store - vuex store
 */
export function genStateComputeds<
	StoreState extends EditStoreBase,
>(state: StoreState):
Accessors<StoreState & StoreState['edit'] & StoreState['localState']> {
	return {
		..._.mapValues(state, (keyValue, key: string, object:any) => getterAndSetterFromKeyDvm(key, object)),
		..._.mapValues(state.edit, (keyValue, key: string, object: any) => getterAndSetterFromKeyDvm(`edit.${key}`, object)),
		..._.mapValues(state.localState, (keyValue, key: string, object: any) => getterAndSetterFromKeyDvm(`localState.${key}`, object)),
	} as Accessors<StoreState & StoreState['edit'] & StoreState['localState']>;
}
export function genVuexComputeds<
	StoreState extends EditStoreBase,
	Getters extends GetterTree<StoreState, RootStore>,
>(store: PageModule<StoreState, any, any, Getters>):
Accessors<StoreState & StoreState['edit'] & StoreState['localState'] & MappedGetters<Getters>> {
	const state = store.state();

	return {
		...genStateComputeds(state),
		..._.mapValues(store.getters, (keyValue, key: string, object: any) => getterFromKeyDvm(key, object)),
	} as Accessors<StoreState & StoreState['edit'] & StoreState['localState'] & MappedGetters<Getters>>;
}


/**
 * Generates Vue component definition. A factory for Edit components.
 *
 * @param name - name of this component.
 * @param store - Vuex store of this component.
 */
export function editFactory<
	TEdit extends object,
	TDropdownList extends DropdownList,
	TLocalState extends object,
	TDependentTables extends Record<string, Table>,
	TProps extends EditStoreProps, Store extends PageModule<TEdit, any, any, any>,
	Actions extends ActionTree<EditStore<TEdit, TDropdownList, TLocalState, TDependentTables, TProps>, RootStore>,
	Mutations extends MutationTree<EditStore<TEdit, TDropdownList, TLocalState, TDependentTables, TProps>>,
	Getters extends GetterTree<EditStore<TEdit, TDropdownList, TLocalState, TDependentTables, TProps>, RootStore>,
>(name: string, store: PageModule<EditStore<TEdit, TDropdownList, TLocalState, TDependentTables, TProps>, Actions, Mutations, Getters>) {
	return Edit.extend(
		{
			name: editName(name),
			computed: {
				...genVuexComputeds(store),
			},
			methods: {
				...mapActionsDvm(store.actions),
				...mapMutationsDvm(store.mutations),
			},
			beforeCreate() {
				initStore(this, editName(name), store);
			},
		},
	);
}
